import React, { useEffect, useState } from "react";
import {
  FileText,
  Settings,
  Activity,
  Download,
  ShieldCheck,
  Database,
  Cpu,
  BarChart3,
  UploadCloud,
  CheckCircle2,
  XCircle,
  ScanLine,
  Zap,
  AlertCircle,
  Info,
  ExternalLink,
  User,
  UserPlus,
  Trash2,
  X,
  CheckCircle,
  ChevronDown,
  ArrowRight,
  Target,
  Layers,
  GraduationCap,
  UserCheck,
  Lock,
  PlusCircle,
} from "lucide-react";


import LiveReportPreview from "./LiveReportPreview";
import { Link, useNavigate } from "react-router-dom";
import Earlyemail from "./Earlyemail";
import EvalHeader from "./EvalHeader";
import Footer from "./footer";

export default function ProfessionalNDT() {
  // Logic remains untouched as requested
  const [config, setConfig] = useState(null);
  const [scan, setScan] = useState(null);
  const [procedure, setProcedure] = useState("ISO_17640_C");
  const [loading, setLoading] = useState(false);
  const [weldImage, setWeldImage] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [indications, setIndications] = useState([]);
  const [activeTab, setActiveTab] = useState("visualization");
  const [scanParams, setScanParams] = useState(null);
  const [defectCount, setDefectCount] = useState(0);
  const [partDetails, setPartDetails] = useState(null);
  const [ascan, setAscan] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const [showUseCases, setShowUseCases] = useState(false);

const [footerModal, setFooterModal] = useState(null);
const [showUploadHint, setShowUploadHint] = useState(false);

  const [showAuth, setShowAuth] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const [notification, setNotification] = useState({
    message: "",
    visible: false,
  });

  const [zoomImage, setZoomImage] = useState(null);

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const API_URL = process.env.REACT_APP_API_URL;

  // State to track report count and modal visibility
const [reportCount, setReportCount] = useState(
    parseInt(localStorage.getItem('evalndt_free_reports') || '0')
);
const [showPaywall, setShowPaywall] = useState(false);
const [user, setUser] = useState(null); // Replace with your actual Auth state/logic

  // Helper to show the error
  const triggerError = (msg) => {
    setNotification({ message: msg, visible: true });
    // Auto-hide after 5 seconds
    setTimeout(() => setNotification({ message: "", visible: false }), 5000);
  };

const navigate = useNavigate();

const handleButtonClick = () => {
  // If no user and limit reached, redirect to login
  if (!user && reportCount >= 1) {
    navigate("/login");
    return;
  }
  
  // Otherwise, run the normal inspection
  runInspection();
};


const runInspection = async () => {
    if (!config || !scan) return;

    // --- REVENUE LOGIC START ---
    // 1. Check if guest has already used their 1 free report
    // (Assuming 'user' is your auth state and 'reportCount' is from localStorage)
    if (!user && reportCount >= 1) {
      setShowPaywall(true); 
      return; // Exit the function before calling the API
    }
    // --- REVENUE LOGIC END ---

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("config", config);
      fd.append("scan", scan);
      if (ascan) fd.append("ascan", ascan);
      fd.append("procedure", procedure);

      const res = await fetch(`${API_URL}/inspect`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) throw new Error("API Error");
      const data = await res.json();

      // --- DATA NORMALIZATION START ---
      const normalizedDefects = (data.defects || []).map((defect) => ({
        ...defect,
        start_mm: Array.isArray(defect.start_mm)
          ? defect.start_mm[0]
          : defect.start_mm || 0,
        end_mm: Array.isArray(defect.end_mm)
          ? defect.end_mm[0]
          : defect.end_mm || 0,
        depth_mm: defect.depth_mm ?? 0,
        status: defect.status || "REJECT",
      }));
      // --- DATA NORMALIZATION END ---

      setWeldImage(`data:image/png;base64,${data.weld_image}`);
      setPdfData(data.pdf);
      setIndications(normalizedDefects); 
      setScanParams(data.scan_parameters);
      setDefectCount(data.defect_count);
      setPartDetails(data.part_details);

      // --- UPDATE REPORT COUNT START ---
      // If the analysis was successful and user is a guest, lock the next try
      if (!user) {
        const newCount = reportCount + 1;
        setReportCount(newCount);
        localStorage.setItem('evalndt_free_reports', newCount.toString());
      }
      // --- UPDATE REPORT COUNT END ---

    } catch (err) {
      console.error("Inspection Error:", err);
      triggerError(
        "Analysis failed: Please ensure you have added proper weld scan files before proceeding."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    if (!pdfData) return;
    const bytes = Uint8Array.from(atob(pdfData), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `NDT_Report_${Date.now()}.pdf`;
    a.click();
  };

  // console.log("pdfData", pdfData);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans text-slate-900">
      {/* 1. TOP NAVIGATION BAR */}


      <EvalHeader />

      {zoomImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomImage(null)}
        >
          <img
            src={zoomImage.src}
            className="max-w-full max-h-full rounded shadow-2xl"
          />
        </div>
      )}

      {notification.visible && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-slate-900 border-l-4 border-red-500 shadow-2xl p-4 flex items-center space-x-4 min-w-[400px]">
            {/* Warning Icon */}
            <div className="bg-red-500/20 p-2 rounded-full">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <div className="flex-1">
              <h3 className="text-white text-xs font-black uppercase tracking-wider">
                Analysis Error
              </h3>
              <p className="text-slate-400 text-[11px] leading-tight mt-0.5">
                {notification.message}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() =>
                setNotification({ ...notification, visible: false })
              }
              className="text-slate-500 hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
        {/* 2. SIDEBAR PANEL - Adjusted w-full for mobile, lg:w-96 for desktop */}
        {/* 2. SIDEBAR PANEL */}
        <aside className="w-full lg:w-96 bg-white border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col p-5 sm:p-6 lg:p-8 overflow-y-auto space-y-8 lg:space-y-10 shadow-sm transition-all duration-300">

          {/* Setup Controls Section */}
          <div className="space-y-6">
            <header className="flex items-center justify-between border-b border-slate-100 pb-2">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                Analysis Setup
              </h3>
              {/* Optional: Mobile indicator that they are in setup mode */}
              <span className="lg:hidden text-[10px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">
                Step 1
              </span>
            </header>

            {/* Acceptance Standard Dropdown */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700 ml-1">
                Acceptance Standard
              </label>
              <div className="relative">
                <select
                  value={procedure}
                  onChange={(e) => setProcedure(e.target.value)}
                  className="w-full h-12 bg-slate-50 border-2 border-slate-100 rounded-xl px-4 font-bold text-slate-700 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all appearance-none"
                >
                  <option value="ISO_17640_C">ISO 17640 (Class C)</option>
                  <option value="ASME">ASME Section V</option>
                </select>
                {/* Custom Arrow for cleaner look */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>

            {/* File Upload Grid: Stacks on mobile, 2-cols on tablet, 1-col on desktop sidebar */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
  {/* Configuration - Professional Indigo */}
  <div className="p-2 bg-indigo-50/60 border border-indigo-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <FileZone
      label="Configuration"
      subLabel="Upload Setup .CSV"
      file={config}
      onClear={() => setConfig(null)}
      onChange={(e) => setConfig(e.target.files[0])}
    />
  </div>

  {/* C-Scan Data - Success Emerald */}
  <div className="p-2 bg-emerald-50/60 border border-emerald-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <FileZone
      label="C-Scan Data"
      subLabel="Upload Raw .TXT"
      file={scan}
      onClear={() => setScan(null)}
      onChange={(e) => setScan(e.target.files[0])}
    />
  </div>

  {/* A-Scan - Technical Amber */}
  <div className="sm:col-span-2 lg:col-span-1 p-2 bg-amber-50/60 border border-amber-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <FileZone
      label="A-Scan (Optional)"
      subLabel=" Upload Waveform .TXT"
      file={ascan}
      onClear={() => setAscan(null)}
      onChange={(e) => setAscan(e.target.files[0])}
    />
  </div>
</div>


            <div className="space-y-4">
              {/* Action Button */}
<button
  // Only disable if currently loading or if files are missing AND it's NOT an "Upgrade" state
  disabled={loading || (!user && reportCount < 1 && (!config || !scan))}
  onClick={handleButtonClick}
  className={`w-full py-5 rounded-2xl text-sm font-black tracking-widest transition-all flex items-center justify-center gap-3 shadow-xl active:scale-[0.98]
    ${(!config || !scan) && (!user && reportCount < 1)
      ? "bg-slate-100 text-slate-400 cursor-not-allowed shadow-none"
      : "bg-indigo-600 hover:bg-indigo-700 text-white hover:scale-[1.01] shadow-indigo-200"
    }`}
>
  {loading ? (
    <span className="flex items-center gap-2">
      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      PROCESSING...
    </span>
  ) : (
    <>
      <Cpu size={18} /> 
      {!user && reportCount >= 1 
        ? "UPGRADE TO RUN" 
        : weldImage 
          ? "RE-RUN ANALYSIS" 
          : "RUN NEW SCAN"
      }
    </>
  )}
</button>

              {/* MOBILE ONLY: Scroll Down Indicator Box */}
              {/* Appears only when data is loaded (weldImage exists) and not currently loading */}
              {weldImage && !loading && (
                <div className="lg:hidden animate-in fade-in slide-in-from-top-4 duration-500">
                  <div
                    onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                    className="group flex items-center justify-between p-4 bg-emerald-50 border-2 border-emerald-100 rounded-2xl cursor-pointer hover:bg-emerald-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-200">
                        <CheckCircle className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Analysis Complete</p>
                        <p className="text-xs font-bold text-slate-700">Scan data is ready below</p>
                      </div>
                    </div>

                    <div className="flex flex-col items-center animate-bounce">
                      <ChevronDown className="text-emerald-500" size={20} />
                      <span className="text-[8px] font-black text-emerald-500 uppercase">View</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Earlyemail />


        </aside>

        {/* 3. MAIN WORKSPACE */}
        <main className="flex-1 flex flex-col bg-[#F8FAFC] overflow-y-auto lg:overflow-hidden">
          {/* TOP METRICS STRIP - Adjusted to wrap on mobile */}
          {/* TOP METRICS STRIP */}
          {/* TOP METRICS STRIP */}
          <div className="bg-white border-b border-slate-200 shadow-sm w-full mt-4 md:mt-6 lg:mt-0 transition-all duration-300">
            {/* Layout Logic: 
      - Mobile: 2x2 grid (Metrics on top, Button/Status on bottom)
      - Desktop (lg): Single horizontal flex row
  */}
            <div className="grid grid-cols-2 lg:flex lg:flex-row lg:h-24 lg:divide-x divide-slate-100">

              <MetricBox
                label="Velocity"
                value={scanParams?.velocity_mps ? `${scanParams.velocity_mps} m/s` : "—"}
                icon={<Zap className="text-amber-500" size={18} />}
                className="border-b border-r lg:border-none border-slate-50"
              />

              <MetricBox
                label="Probe Freq"
                value={partDetails?.frequency_mhz ? `${partDetails.frequency_mhz} MHz` : "—"}
                icon={<Activity className="text-indigo-600" size={18} />}
                className="border-b lg:border-none border-slate-50"
              />

              <MetricBox
                label="Defect Count"
                value={defectCount}
                icon={
                  <AlertCircle
                    size={18}
                    className={defectCount > 0 ? "text-red-500 animate-pulse" : "text-slate-300"}
                  />
                }
                className="border-r lg:border-none border-slate-50"
              />

              {/* CTA SECTION */}
              <div className="lg:flex-1 flex items-center justify-center lg:justify-end px-4 py-4 lg:px-10 bg-slate-50/50 lg:bg-transparent">
                {pdfData ? (
                  <button
                    onClick={downloadPdf}
                    className="w-full lg:w-auto flex items-center justify-center gap-3 bg-slate-900 text-white px-6 lg:px-8 py-3.5 lg:py-4 rounded-xl lg:rounded-2xl text-[10px] lg:text-xs font-black hover:bg-indigo-600 transition-all shadow-lg active:scale-95"
                  >
                    <Download size={18} />
                    <span className="uppercase tracking-widest">Download Result</span>
                  </button>
                ) : (
                  <div className="flex flex-col items-center lg:items-end">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Report Status</span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Waiting for Scan...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SCROLLABLE VIEWPORT */}
          <div className="flex-1 p-4 lg:p-10 overflow-y-auto space-y-8">
            {/* DATA GRID - grid-cols-1 on mobile, grid-cols-3 on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <InfoCard
                title="Workpiece Specs"
                items={[
                  ["Material", partDetails?.material],
                  [
                    "Dimensions",
                    partDetails?.thickness_mm
                      ? `${partDetails.thickness_mm}x${partDetails.length_mm}mm`
                      : null,
                  ],
                  ["Weld Profile", partDetails?.weld_type],
                ]}
              />
              <InfoCard
                title="Hardware Config"
                items={[
                  ["Probe Model", partDetails?.probe_model],
                  [
                    "Wedge Angle",
                    partDetails?.wedge_angle
                      ? `${partDetails.wedge_angle}°`
                      : null,
                  ],
                  ["Serial ID", partDetails?.probe_serial],
                ]}
              />
              <InfoCard
                title="Scan Settings"
                items={[
                  [
                    "System Gain",
                    scanParams?.gain_db ? `${scanParams.gain_db} dB` : null,
                  ],
                  [
                    "Index Range",
                    scanParams?.index_start
                      ? `${scanParams.index_start}° - ${scanParams.index_qty}`
                      : null,
                  ],
                  ["Procedure", scanParams?.procedure],
                ]}
              />
            </div>

            {/* VISUALIZATION CONTAINER - Responsive height */}
            <div className="bg-white rounded-[1.5rem] lg:rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col min-h-[500px] lg:h-[700px]">
              {/* Tabs Header - overflow-x-auto for small screens */}
<div className="flex bg-white px-4 lg:px-10 border-b border-slate-200 shrink-0 overflow-x-auto no-scrollbar items-center">
  <div className="flex gap-2 py-2">
    {/* VISUALIZER TAB */}
    <TabBtn
      active={activeTab === "visualization"}
      onClick={() => setActiveTab("visualization")}
      icon={<ScanLine size={16} />}
      label="C-SCAN VISUALIZER"
      className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all duration-200 border ${
        activeTab === "visualization"
          ? "bg-[#1E3A8A]/5 border-[#1E3A8A]/20 text-[#1E3A8A] shadow-sm"
          : "bg-transparent border-transparent text-slate-400 hover:bg-slate-50 hover:text-slate-600"
      }`}
    />

    {/* DATA TAB */}
    <TabBtn
      active={activeTab === "indications"}
      onClick={() => setActiveTab("indications")}
      icon={<Database size={16} />}
      label="INDICATION DATA"
      className={`flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all duration-200 border ${
        activeTab === "indications"
          ? "bg-[#1E3A8A]/5 border-[#1E3A8A]/20 text-[#1E3A8A] shadow-sm"
          : "bg-transparent border-transparent text-slate-400 hover:bg-slate-50 hover:text-slate-600"
      }`}
    />
  </div>

  {/* DIVIDER */}
  <div className="w-[1px] h-6 bg-slate-200 mx-4" /> 

  {/* REPORT LINK */}
<Link
  to="/reporteditor"
  state={{ indications, partDetails, scanParams, weldImage }}
  className={`flex shrink-0 items-center gap-3 px-6 py-2.5 rounded-xl border transition-all group ${
    pdfData 
      ? "border-green-100 bg-green-50/40 text-green-800 shadow-sm" 
      : "border-blue-100/50 bg-blue-50/30 text-[#1E3A8A]"
  }`}
  style={!pdfData ? { animation: 'subtle-pulse 3s infinite ease-in-out' } : {}}
>
  <div className="relative p-1 rounded-md bg-white border border-blue-100 shadow-sm">
    <FileText size={14} className={pdfData ? "text-green-600" : "text-[#1E3A8A]"} />
    
    {/* LIVE DOT: Only renders and blinks when pdfData exists */}
    {pdfData && (
      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 border border-white"></span>
      </span>
    )}
  </div>

  <span className="text-[10px] font-black uppercase tracking-[0.2em]">
    {pdfData ? "Live Report Ready • View" : "Live Report Preview"}
  </span>
  
  <ExternalLink size={12} className={`transition-opacity ${pdfData ? "opacity-100" : "opacity-40 group-hover:opacity-100"}`} />

  <style dangerouslySetInnerHTML={{ __html: `
    @keyframes subtle-pulse {
      0% { background-color: rgba(239, 246, 255, 0.3); border-color: rgba(219, 234, 254, 0.5); }
      50% { background-color: rgba(239, 246, 255, 0.8); border-color: rgba(30, 58, 138, 0.2); }
      100% { background-color: rgba(239, 246, 255, 0.3); border-color: rgba(219, 234, 254, 0.5); }
    }
  `}} />
</Link>
</div>

              {/* Content Area */}
              <div className="flex-1 min-h-0 p-4">
                {/* ================= VISUALIZATION TAB ================= */}
                {activeTab === "visualization" && (
                  <div
                    className="relative w-full h-[500px] lg:h-full bg-[#0F172A] rounded-[1.5rem]
      border border-slate-800 shadow-2xl overflow-hidden
      flex items-center justify-center transition-all duration-300"
                  >
                    {!weldImage ? (
                      /* Awaiting Data State */
<div
  onClick={() => setShowUploadHint(true)}
  className="text-center relative z-10 p-6 cursor-pointer select-none"
>
  <div className="relative mb-6">
    <div className="absolute inset-0 rounded-full bg-indigo-500/20 animate-ping" />
    <div
      className="w-16 h-16 lg:w-24 lg:h-24 bg-slate-800 rounded-full flex items-center justify-center
      mx-auto border-2 border-slate-700 shadow-xl relative z-10"
    >
      <ScanLine size={32} className="text-indigo-400 lg:hidden" />
      <ScanLine size={48} className="text-indigo-400 hidden lg:block" />
    </div>
  </div>

  <h3 className="text-sm lg:text-xl font-black text-white uppercase tracking-[0.2em] mb-2">
    Ready for Scan
  </h3>

  <div className="flex items-center justify-center gap-2">
    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
    <p className="text-[8px] lg:text-[10px] font-black text-slate-500 uppercase tracking-widest">
      Awaiting Signal
    </p>
  </div>

  {showUploadHint && (
    <div className="mt-4 px-4 py-2 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
      <p className="text-[10px] lg:text-[11px] font-black text-indigo-400 uppercase tracking-widest">
        Please upload inspection files to generate results
      </p>
    </div>
  )}
</div>

                    ) : (
                      /* Image View */
                      <div className="relative w-full h-full flex items-center justify-center p-4 lg:p-6">
                        <img
                          src={weldImage}
                          alt="NDT Visualization"
                          className="max-w-full max-h-full object-contain rounded-lg
                 drop-shadow-[0_0_30px_rgba(79,70,229,0.25)]"
                        />

                        {/* Overlay Info - Stacked on mobile */}
                        <div className="absolute top-4 left-4 lg:top-6 lg:left-6 flex flex-col lg:flex-row gap-2 lg:gap-4">
                          <div className="px-3 py-1 lg:px-4 lg:py-2 bg-black/60 backdrop-blur border border-white/10 rounded-lg">
                            <p className="text-[7px] lg:text-[8px] font-black text-indigo-400 uppercase tracking-widest">Resolution</p>
                            <p className="text-[10px] lg:text-xs font-bold text-white">HI-RES</p>
                          </div>

                          <div className="px-3 py-1 lg:px-4 lg:py-2 bg-black/60 backdrop-blur border border-white/10 rounded-lg">
                            <p className="text-[7px] lg:text-[8px] font-black text-indigo-400 uppercase tracking-widest">Source</p>
                            <p className="text-[10px] lg:text-xs font-bold text-white uppercase truncate max-w-[80px] lg:max-w-none">{procedure}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Grid Overlay */}
                    <div
                      className="absolute inset-0 pointer-events-none opacity-[0.05]"
                      style={{
                        backgroundImage:
                          "linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)",
                        backgroundSize: "40px 40px",
                      }}
                    />
                  </div>
                )}

                {/* ================= INDICATION LOG TAB ================= */}
                {activeTab === "indications" && (
                  <div
                    className="w-full h-[600px] lg:h-full bg-white rounded-[1.5rem]
      border border-slate-200 shadow-xl overflow-hidden"
                  >
                    <IndicationLog
                      indications={indications}
                      setIndications={setIndications}
                      pdfData={pdfData}
                      downloadPdf={downloadPdf}
                    />
                  </div>
                )}

                {/* ================= REPORT TAB ================= */}
                {activeTab === "report" && (
                  <div className="w-full h-[700px] lg:h-full bg-white rounded-[1.5rem] border border-slate-200 shadow-xl overflow-hidden p-4">
                    <LiveReportPreview
                      indications={indications}
                      setIndications={setIndications}
                      partDetails={partDetails}
                      setPartDetails={setPartDetails}
                      scanParams={scanParams}
                      setScanParams={setScanParams}
                      weldImage={weldImage}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* BOTTOM FOOTER AD - Layout changed to flex-col on mobile */}
            {/* BOTTOM FOOTER AD - Strategic Pre-Launch Version */}
            <div className="bg-slate-900 border-2 border-indigo-500/20 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden group">

              {/* Subtle Background Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/10 blur-[80px] rounded-full -mr-20 -mt-20 pointer-events-none" />

              <div className="flex flex-col md:flex-row items-center text-center md:text-left gap-4 lg:gap-6 relative z-10">
                <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center shadow-inner shrink-0 group-hover:scale-110 transition-transform duration-500">
                  <BarChart3 className="text-indigo-400" size={32} />
                </div>

                <div>
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-indigo-500 text-[8px] font-black text-white uppercase rounded tracking-widest">
                      Coming Soon
                    </span>
                    <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-widest">
                      Enterprise Node
                    </span>
                  </div>
                  <h5 className="text-xl font-black text-white tracking-tight">
                    Enterprise Cloud Reporting
                  </h5>
                  <p className="text-sm font-medium text-slate-400 max-w-sm">
                    Sync diagnostic data to our secured cloud. Join the waiting list for early API access.
                  </p>
                </div>
              </div>

              {/* <button 
    onClick={() => {
      // Logic to scroll to your Earlyemail.jsx form or open a modal
      document.getElementById('early-access-form')?.scrollIntoView({ behavior: 'smooth' });
    }}
    className="w-full md:w-auto px-10 py-4 bg-white text-slate-900 text-[10px] font-black uppercase rounded-2xl hover:bg-indigo-50 transition-all active:scale-95 flex items-center justify-center gap-2 shadow-xl"
  >
    Request Beta Access <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
  </button> */}
            </div>
          </div>
        </main>
        
      </div>


<Footer />

    </div>
  );
}

// --- REFINED UI COMPONENTS ---

function FileZone({ label, subLabel, file, onChange, onClear }) {
  return (
<div
  className={`relative border-2 border-dashed rounded-2xl p-4 transition-all
    ${file
      ? "border-emerald-500 bg-emerald-50/50"
      : "border-indigo-300 bg-indigo-50/30 hover:border-indigo-900 hover:bg-indigo-50/50"
    }`}
>

      <div className="flex items-center gap-4">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${file ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"
            }`}
        >
          {file ? <CheckCircle size={20} /> : <UploadCloud size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
            {label}
          </p>
          <p className="text-sm font-bold text-slate-800 truncate">
            {file ? file.name : subLabel}
          </p>
        </div>
        {file && (
          <button
            onClick={onClear}
            className="p-2 text-slate-300 hover:text-red-500 transition-colors"
          >
            <XCircle size={20} />
          </button>
        )}
      </div>
      {!file && (
        <input
          type="file"
          onChange={onChange}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      )}
    </div>
  );
}

function MetricBox({ label, value, icon }) {
  return (
    <div className="px-10 flex items-center gap-5 group border-r border-slate-50">
      <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
        {React.cloneElement(icon, { size: 24 })}
      </div>
      <div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1 leading-none">
          {label}
        </p>
        <p className="text-2xl font-black text-slate-800 leading-none tracking-tight">
          {value}
        </p>
      </div>
    </div>
  );
}

function InfoCard({ title, items }) {
  return (
    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
        <div className="w-1 h-4 bg-indigo-500 rounded-full" /> {title}
      </h4>
      <div className="space-y-3">
        {items.map(([l, v], i) => (
          <div
            key={i}
            className="flex justify-between items-end border-b border-slate-50 pb-1"
          >
            <span className="text-xs font-bold text-slate-400">{l}</span>
            <span className="text-sm font-black text-slate-800">
              {v || "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`py-6 text-xs font-black uppercase tracking-[0.2em] transition-all border-b-4 mr-12 flex items-center gap-3 ${active
          ? "border-indigo-600 text-indigo-600"
          : "border-transparent text-slate-400 hover:text-slate-600"
        }`}
    >
      {icon} {label}
    </button>
  );
}

function IndicationLog({ indications, setIndications, pdfData, downloadPdf }) {
  // Enhanced HandleEdit to ensure numbers stay numbers and strings stay strings
  const handleEdit = (id, field, value) => {
    const updated = indications.map((ind) => {
      if (ind.id === id) {
        // If it's a numeric field, parse it; otherwise keep as string
        const isNumeric = [
          "amplitude_percent",
          "start_mm",
          "end_mm",
          "depth_mm",
        ].includes(field);
        const processedValue = isNumeric ? parseFloat(value) || 0 : value;
        return { ...ind, [field]: processedValue };
      }
      return ind;
    });
    setIndications(updated);
  };

  // Helper to remove an indication if it's a false positive
  const deleteIndication = (id) => {
    setIndications(indications.filter((ind) => ind.id !== id));
  };

  return (
    <div className="flex flex-col h-full bg-white animate-in fade-in duration-500">
      {/* Action Header */}
      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <div>
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">
            Automated Indication Registry
          </h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            Review and validate detected flaws below
          </p>
        </div>

        {pdfData && (
          <button
            onClick={downloadPdf}
            className="flex items-center gap-3 bg-slate-900 text-white px-6 py-3 rounded-xl text-[10px] font-black hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200 animate-in slide-in-from-right-4"
          >
            <Download size={16} /> DOWNLOAD FINAL PDF REPORT
          </button>
        )}
      </div>

      {/* Editable Data Table */}
      <div className="flex-1 overflow-auto">
        {indications.length > 0 ? (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white z-10 shadow-sm">
              <tr className="border-b border-slate-200">
                {[
                  "ID",
                  "Amp (%)",
                  "Start (mm)",
                  "End (mm)",
                  "Depth (mm)",
                  "Status",
                  "",
                ].map((head) => (
                  <th
                    key={head}
                    className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {indications.map((ind) => (
                <tr
                  key={ind.id}
                  className="hover:bg-indigo-50/30 transition-colors group"
                >
                  {/* Editable ID (Text) */}
                  <td className="px-8 py-4">
                    <input
                      type="text"
                      value={ind.id}
                      onChange={(e) => handleEdit(ind.id, "id", e.target.value)}
                      className="w-16 bg-transparent border-none text-xs font-black text-slate-500 focus:ring-1 focus:ring-indigo-500 rounded p-1"
                    />
                  </td>

                  {/* Editable Amplitude (Numeric) */}
                  <td className="px-8 py-4">
                    <input
                      type="number"
                      step="0.1"
                      value={ind.amplitude_percent}
                      onChange={(e) =>
                        handleEdit(ind.id, "amplitude_percent", e.target.value)
                      }
                      className="w-20 bg-transparent border-none text-xs font-bold text-slate-700 focus:ring-1 focus:ring-indigo-500 rounded p-1"
                    />
                  </td>

                  {/* Editable Start Position (Numeric) */}
                  <td className="px-8 py-4">
                    <input
                      type="number"
                      step="0.01"
                      value={ind.start_mm}
                      onChange={(e) =>
                        handleEdit(ind.id, "start_mm", e.target.value)
                      }
                      className="w-24 bg-transparent border-none text-xs font-bold text-slate-700 focus:ring-1 focus:ring-indigo-500 rounded p-1"
                    />
                  </td>

                  {/* Editable End Position (Numeric) */}
                  <td className="px-8 py-4">
                    <input
                      type="number"
                      step="0.01"
                      value={ind.end_mm}
                      onChange={(e) =>
                        handleEdit(ind.id, "end_mm", e.target.value)
                      }
                      className="w-24 bg-transparent border-none text-xs font-bold text-slate-700 focus:ring-1 focus:ring-indigo-500 rounded p-1"
                    />
                  </td>

                  {/* Editable Depth (Numeric) */}
                  <td className="px-8 py-4">
                    <input
                      type="number"
                      step="0.1"
                      value={ind.depth_mm}
                      onChange={(e) =>
                        handleEdit(ind.id, "depth_mm", e.target.value)
                      }
                      className="w-20 bg-transparent border-none text-xs font-bold text-slate-700 focus:ring-1 focus:ring-indigo-500 rounded p-1"
                    />
                  </td>

                  {/* Status Toggle */}
                  <td className="px-8 py-4">
                    <button
                      onClick={() =>
                        handleEdit(
                          ind.id,
                          "status",
                          ind.status === "REJECT" ? "ACCEPT" : "REJECT"
                        )
                      }
                      className={`px-4 py-1.5 rounded-full text-[9px] font-black tracking-widest transition-all ${ind.status === "REJECT"
                          ? "bg-red-50 text-red-600 border border-red-100 hover:bg-red-100"
                          : "bg-emerald-50 text-emerald-600 border border-emerald-100 hover:bg-emerald-100"
                        }`}
                    >
                      {ind.status || "ACCEPT"}
                    </button>
                  </td>

                  {/* Delete Action */}
                  <td className="px-8 py-4 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => deleteIndication(ind.id)}
                      className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-300 p-20">
            <ScanLine size={48} className="mb-4 opacity-20" />
            <p className="text-xs font-black uppercase tracking-widest">
              No Indications Detected
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const FOOTER_CONTENT = {
  roadmap: {
    title: 'Precision Engineering Roadmap',
    content: (
      <div className="space-y-8">
        <div className="space-y-3">
          <p className="text-sm md:text-base text-slate-600 font-medium leading-relaxed">
            EvalNDT is built on a modular architecture designed for high-frequency signal analysis. 
            Our 2026 milestones prioritize automation without sacrificing Level III oversight.
          </p>
        </div>
        
<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
  {[
    {
      label: 'Signal Reconstruction',
      status: 'Active',
      desc: 'High-fidelity parsing of S-Scan and A-Scan data sets with synchronized encoded position tracking.',
      color: 'bg-emerald-500'
    },
    {
      label: 'Sensitivity Calibration',
      status: 'Stable',
      desc: 'Amplitude normalization via TCG (Time Corrected Gain) to ensure uniform sensitivity across the full focal range.',
      color: 'bg-blue-500'
    },
    {
      label: 'Indication Profiling',
      status: 'In-Test',
      desc: 'S-Scan based defect characterization using peak amplitude and -6dB drop logic for precise sizing.',
      color: 'bg-indigo-500'
    },
    {
      label: 'Compliance Engine',
      status: 'Stable',
      desc: 'Generation of traceable PAUT reports including S-Scan snapshots, A-Scan gates, and TCG calibration records.',
      color: 'bg-emerald-500'
    }
  ].map((item, i) => (
    <div
      key={i}
      className="flex flex-col p-6 bg-slate-50 rounded-[2rem] border-2 border-slate-100 hover:border-indigo-400 transition-all"
    >
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] md:text-[13px] font-black text-slate-900 uppercase tracking-tight">
          {item.label}
        </span>
        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${item.color}`} />
          {item.status}
        </span>
      </div>
      <p className="text-[11px] md:text-[13px] text-slate-600 leading-relaxed font-medium">
        {item.desc}
      </p>
    </div>
  ))}
</div>


      </div>
    )
  },

  privacy: {
    title: 'Privacy & Information Security',
    content: (
      <div className="space-y-6">
        <div className="bg-slate-900 p-6 rounded-3xl text-white relative overflow-hidden">
          <div className="relative z-10">
            <h4 className="text-xs md:text-sm font-black uppercase tracking-widest text-indigo-400 mb-2">Security Architecture</h4>
            <p className="text-sm md:text-base font-medium opacity-90 leading-relaxed">
              We employ military-grade AES-256 encryption for data in transit and strictly enforce a non-persistent session policy.
            </p>
          </div>
          <div className="absolute -right-4 -bottom-4 opacity-10 text-6xl">🔒</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h5 className="text-[10px] md:text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em]">User Protections</h5>
            <ul className="space-y-3">
              {[
                { t: 'Zero Profiling', d: 'No behavioral tracking or metadata harvesting.' },
                { t: 'No Third-Party Access', d: 'Your identity is never shared with OEMs or partners.' }
              ].map((item, i) => (
                <li key={i} className="space-y-1">
                  <p className="text-xs md:text-sm font-black text-slate-800">{item.t}</p>
                  <p className="text-[11px] md:text-xs text-slate-500 font-medium">{item.d}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-4">
            <h5 className="text-[10px] md:text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em]">Audit Readiness</h5>
            <ul className="space-y-3">
              {[
                { t: 'Immutable Logs', d: 'Session logs for internal quality audits only.' },
                { t: 'GDPR / CCPA', d: 'Full compliance with global data right standards.' }
              ].map((item, i) => (
                <li key={i} className="space-y-1">
                  <p className="text-xs md:text-sm font-black text-slate-800">{item.t}</p>
                  <p className="text-[11px] md:text-xs text-slate-500 font-medium">{item.d}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    )
  },

  data: {
    title: 'Data Sovereignty & Compliance',
    content: (
      <div className="space-y-6">
        <div className="p-1 bg-indigo-50 rounded-2xl border border-indigo-100">
          <div className="bg-white p-5 rounded-[14px] shadow-sm">
            <p className="text-sm md:text-lg font-black text-slate-800 leading-snug">
              Inspection data is the <span className="text-indigo-600">User's Exclusive Intellectual Property.</span>
            </p>
            <p className="text-[11px] md:text-sm text-slate-500 mt-2 font-medium">
              EVALNDT acts solely as a processing layer. We do not claim ownership of indications, scans, or generated logic.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { h: 'No AI Training', p: 'User data is strictly excluded from machine learning sets.' },
            { h: 'Volatile Memory', p: 'Files reside in RAM during analysis and are purged on exit.' },
            { h: 'Encrypted Export', p: 'Reports are generated with SHA-256 checksums for integrity.' },
            { h: 'Zero Persistence', p: 'No local storage cookies or long-term data caching.' }
          ].map((item, i) => (
            <div key={i} className="p-4 border border-slate-100 rounded-2xl hover:bg-slate-50 transition-colors">
              <h5 className="text-[10px] md:text-xs font-black text-slate-800 uppercase mb-1">{item.h}</h5>
              <p className="text-[11px] md:text-xs text-slate-500 font-medium leading-tight">{item.p}</p>
            </div>
          ))}
        </div>
      </div>
    )
  },

  contact: {
    title: 'Technical Support Gateway',
    content: (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="space-y-4 text-center md:text-left">
            <h4 className="text-sm md:text-lg font-black text-slate-800 leading-tight">
              Direct Access to Specialized Engineering Support.
            </h4>
            <p className="text-xs md:text-sm text-slate-500 font-medium">
              Our support team consists of NDT software specialists and Level III consultants.
            </p>
          </div>
          
          <div className="p-6 bg-slate-900 rounded-[2.5rem] shadow-2xl border border-slate-800 relative group">
            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Central Node</p>
            <p className="text-lg md:text-2xl font-mono text-white break-all select-all group-hover:text-indigo-200 transition-colors">
              support@evalndt.com
            </p>
            <div className="mt-4 flex gap-2">
              <div className="px-3 py-1 bg-white/5 rounded-full text-[9px] font-black text-white/40 uppercase tracking-widest">Global</div>
              <div className="px-3 py-1 bg-white/5 rounded-full text-[9px] font-black text-white/40 uppercase tracking-widest">24hr Monitor</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 p-6 bg-amber-50/50 border border-amber-100 rounded-[2rem]">
          <div className="text-3xl md:text-4xl shrink-0 text-center">📋</div>
          <div>
            <p className="text-xs md:text-sm font-black text-amber-900 uppercase mb-1 tracking-tight">Expedited Ticket Requirements</p>
            <p className="text-[11px] md:text-[13px] text-amber-800/80 leading-relaxed font-medium">
              To bypass standard triage, please provide: <span className="font-bold underline">Acquisition Hardware Model</span>, 
              <span className="font-bold underline">Scan File Extension</span> (.uvdata, .rdt, etc.), and <span className="font-bold underline">Method Code</span>.
            </p>
          </div>
        </div>
      </div>
    )
  }
};

